import { MenuLink } from '@easeltv/client-sdk';
import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '../../providers/AuthProvider/AuthProvider';
import { useApi } from '../useApi/useApi';

export const MenuLinkGroupId = {
    navigation: 'menu-navigation',
    cta: 'menu-call-to-action',
    loggedIn: 'menu-settings-logged-in',
    loggedOut: 'menu-settings-logged-out',
};

export type Menus = {
    [key in keyof typeof MenuLinkGroupId]: MenuLink[];
};

export function useGetMenu(isMicrosite: boolean) {
    const api = useApi();
    const { registerObserver, detachObserver } = useAuth();

    const [menu, setMenu] = useState<Menus>({
        navigation: [],
        cta: [],
        loggedIn: [],
        loggedOut: [],
    });

    const getMenus = useCallback(async () => {
        if (isMicrosite) return;

        const result = await api.getMenu({ extend: ['pages'] });
        if (!result.ok) {
            // @TODO: To be removed in place of proper error tracking
            // eslint-disable-next-line no-console
            console.trace(result);
            // @TODO: Error handling
            return;
        }
        const response = result.payload;

        if (!response.body.extend) {
            // @TODO: Error handling
            return;
        }

        const elementArray = response.body.links.reduce<Menus>(
            (menus, element) => {
                switch (element.linkGroupId) {
                    case MenuLinkGroupId.navigation:
                        return {
                            ...menus,
                            navigation: [...menus.navigation, element],
                        };
                    case MenuLinkGroupId.cta:
                        return {
                            ...menus,
                            cta: [...menus.cta, element],
                        };
                    case MenuLinkGroupId.loggedIn:
                        return {
                            ...menus,
                            loggedIn: [...menus.loggedIn, element],
                        };
                    case MenuLinkGroupId.loggedOut:
                        return {
                            ...menus,
                            loggedOut: [...menus.loggedOut, element],
                        };
                    default:
                        return menus;
                }
            },
            {
                navigation: [],
                cta: [],
                loggedIn: [],
                loggedOut: [],
            },
        );

        setMenu(elementArray);
    }, [api, isMicrosite]);

    useEffect(() => {
        getMenus();
    }, [getMenus]);

    useEffect(() => {
        const observeFn = () => {
            getMenus();
        };

        registerObserver(observeFn);

        return () => {
            detachObserver(observeFn);
        };
    }, [detachObserver, getMenus, registerObserver]);

    return menu;
}
